import { useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import card_services from "services/card_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { apiErrorMessage } from "utils/errors/parseApiError";

export type CardStatusAction = "suspend" | "activate";

export interface UseCardStatusToggleReturn {
  changingCardStatus: boolean;
  changingCardStatusMsg: string;
  cardStatusError: boolean;
  cardStatusErrorMsg: string;
  cardStatusSuccess: boolean;
  cardStatusSuccessMsg: string;
  handleCardStatusChange: (
    action: CardStatusAction,
    cardNumber: string,
  ) => Promise<void>;
  handleRetry: () => Promise<void>;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
}

/**
 * Suspend (Active -> Suspended) and its reverse, which the chain allows only between those two
 * states — every other status is refused by /suspendCard and /activateCard with a 422.
 */
export function useCardStatusToggle(): UseCardStatusToggleReturn {
  const networkType = useSelector(networkTypeSelector);
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [lastRequest, setLastRequest] = useState<{
    action: CardStatusAction;
    cardNumber: string;
  } | null>(null);

  const openError = (msg: string) => {
    setLoadingMsg("");
    setIsLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMsg("");
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
  };

  const handleCardStatusChange = async (
    action: CardStatusAction,
    cardNumber: string,
  ) => {
    const suspending = action === "suspend";
    setLastRequest({ action, cardNumber });
    // ErrorModal's "Try again" does not dismiss itself, so a retry would stack the next modal
    // on top of the open error.
    handleCloseError();

    try {
      setLoadingMsg(suspending ? "Suspending card…" : "Activating card…");
      setIsLoading(true);

      const result: any = suspending
        ? await card_services.suspendCard(cardNumber, networkType)
        : await card_services.activateCard(cardNumber, networkType);

      // The row reads `status` from the list query, so without this the badge — and this very
      // menu item — keep offering the action that just succeeded.
      queryClient.invalidateQueries({ queryKey: ["cards"] });
      // Marked stale but NOT refetched: useCardLookup reports isFetching as "loading", which
      // swaps the searched row for a skeleton and unmounts the success modal below.
      queryClient.invalidateQueries({
        queryKey: ["card-lookup"],
      });
      queryClient.invalidateQueries({ queryKey: ["balances", "cards"] });
      queryClient.invalidateQueries({ queryKey: ["balances", "cards"] });

      setIsLoading(false);
      setLoadingMsg("");
      setSuccessMsg(
        result?.alreadyApplied
          ? `Card ${cardNumber} was already ${suspending ? "suspended" : "active"}.`
          : `Card ${cardNumber} has been ${suspending ? "suspended" : "activated"}.`,
      );
      setShowSuccess(true);
    } catch (error: any) {
      openError(
        apiErrorMessage(
          error,
          suspending
            ? "Failed to suspend card. Please try again."
            : "Failed to activate card. Please try again.",
        ),
      );
    }
  };

  const handleRetry = () =>
    lastRequest
      ? handleCardStatusChange(lastRequest.action, lastRequest.cardNumber)
      : Promise.resolve();

  return {
    changingCardStatus: isLoading,
    changingCardStatusMsg: loadingMsg,
    cardStatusError: showError,
    cardStatusErrorMsg: errorMsg,
    cardStatusSuccess: showSuccess,
    cardStatusSuccessMsg: successMsg,
    handleCardStatusChange,
    handleRetry,
    handleCloseError,
    handleCloseSuccess,
  };
}
